import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2, XCircle, Info, Shield } from "lucide-react";

/*  Public, transparent explainer for how the leaderboard scores.
    Pulls the live scoring config from the backend so the words on this
    page can never drift from how the system actually ranks.
*/

const Block = ({ title, children, kind = "info" }) => {
  const icons = {
    info: <Info size={16} className="text-[#d4af37]" />,
    yes:  <CheckCircle2 size={16} className="text-[#4ade80]" />,
    no:   <XCircle size={16} className="text-[#f87171]" />,
    shield: <Shield size={16} className="text-[#d4af37]" />,
  };
  return (
    <section className="card-noir p-5 mb-4">
      <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.3em] text-[#d4af37]">
        {icons[kind]} <span>{title}</span>
      </div>
      <div className="text-sm text-[#f4f0ec] leading-relaxed">
        {children}
      </div>
    </section>
  );
};

const LeaderboardHowItWorks = () => {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/nye/scoring-info")
      .then((r) => setInfo(r.data))
      .catch(() => setErr("Couldn't load scoring details."));
  }, []);

  return (
    <div className="min-h-screen bg-[#050403] text-[#f4f0ec] pb-20"
      data-testid="leaderboard-how-page">
      <div className="border-b border-[#2d2424] bg-[#0a0908]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/leaderboard"
            className="text-xs text-[#a39797] hover:text-[#f4f0ec] flex items-center gap-1.5"
            data-testid="how-back">
            <ArrowLeft size={14} /> Back to leaderboard
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            SCORING · PHASE 1
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 pt-8">
        <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec] leading-tight">
          How the leaderboard works
        </h1>
        <p className="text-sm text-[#a39797] mt-2 mb-6">
          Transparent scoring. No hidden multipliers. No simulated numbers.
        </p>

        {err && (
          <div className="text-sm text-[#f87171] mb-4" data-testid="how-error">{err}</div>
        )}

        {info && (
          <>
            <Block title="HOW COUNTRIES ARE RANKED" kind="info">
              {info.ranking_description}
            </Block>

            <Block title="WHAT COUNTS AS A DETECTIVE" kind="yes">
              <p>
                Every detective is a <strong className="text-[#d4af37]">unique email reservation</strong> that
                successfully completes the Global First Run sign-up form.
              </p>
              <p className="mt-2 text-[#a39797]">
                {info.duplicate_handling}
              </p>
            </Block>

            <Block title="HOW RANKING CHANGES ARE CALCULATED" kind="info">
              <p>
                {info.delta_description}
              </p>
              <ul className="list-disc pl-5 mt-2 text-[#a39797] space-y-1">
                <li>Up arrow (<span className="text-[#4ade80]">▲</span>) — climbed that many places vs. 7 days ago.</li>
                <li>Down arrow (<span className="text-[#f87171]">▼</span>) — dropped that many places vs. 7 days ago.</li>
                <li>Dash (—) — unchanged, new country, or no 7-day history yet. We never guess.</li>
              </ul>
            </Block>

            <Block title="VERIFIED TEAMS" kind={info.verified_teams.included_in_score ? "yes" : "no"}>
              <p>{info.verified_teams.description}</p>
              <p className="mt-2 text-[#5a5050] text-xs">
                Status: {info.verified_teams.status}
                {info.verified_teams.included_in_score
                  ? " · counted in rank"
                  : " · NOT counted in rank"}.
              </p>
            </Block>

            <Block title="REFERRAL REWARDS" kind={info.referral_rewards.enabled ? "yes" : "no"}>
              <p>{info.referral_rewards.description}</p>
            </Block>

            <Block title="FOUNDING DETECTIVE" kind="shield">
              <p>{info.founding_detective.description}</p>
              <p className="mt-2 text-[#5a5050] text-xs">
                Cutoff: first {info.founding_detective.cutoff.toLocaleString()} unique reservations
                worldwide. Shown on your share card automatically if you qualify.
              </p>
            </Block>

            <Block title="FAIRNESS & INTEGRITY" kind="shield">
              <ul className="list-disc pl-5 text-[#a39797] space-y-1">
                <li>One reservation per email. Duplicate sign-ups don't bump the count.</li>
                <li>Rankings use only real recorded reservations — no simulated data, no filler.</li>
                <li>Private information (emails, names, IPs) is never shown on the leaderboard or ticker.</li>
                <li>Referral counts are read-only display: they do not grant points or ranking boosts in Phase 1.</li>
              </ul>
            </Block>

            <div className="text-xs text-[#5a5050] mt-6" data-testid="how-last-updated">
              Scoring config last loaded: {new Date(info.last_updated).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardHowItWorks;
